import React, { useState, useEffect, useRef } from "react";
import { Trash2, X, Plus, ChevronDown } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const ScopeOfWork = ({
  workScopes,
  selectedWorkScope,
  setSelectedWorkScope,  
  selectedTurbines,
  setSelectedTurbines,
  fetchInstructions,
  instToView,
  setInstToView,
  setIsExpanded 
}) => {
  // Logowanie zaznaczonych turbin i wybranego zakresu pracy
  useEffect(() => {
    console.log("Zaznaczone turbiny (IDs):", selectedTurbines);
    console.log("Wybrany zakres pracy:", selectedWorkScope);
  }, [selectedTurbines, selectedWorkScope]);

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Stan przechowujący kroki
  const [sections, setSections] = useState([
    {
      title: "",
      mark: "",
      steps: [
        { 
          field1: "", 
          field2: "", 
          field3: "", 
          field4: "", 
          field5: "", 
          field6: "", 
          requiresPhoto: false, 
          photoInstructions: "" 
        },
      ],
    },
  ]);

  // Dodawanie nowej sekcji
  const addSection = () => {
    setSections((prevSections) => [
      ...prevSections,
      {
        title: "",
        mark: "",
        steps: [
          { 
            field1: "", 
            field2: "", 
            field3: "", 
            field4: "", 
            field5: "", 
            field6: "", 
            requiresPhoto: false, 
            photoInstructions: "" 
          },
        ],
      },
    ]);
  };

  // Dodawanie kroku do sekcji
  const addStepToSection = (sectionIndex) => {
    setSections((prevSections) =>
      prevSections.map((section, index) =>
        index === sectionIndex
          ? {
              ...section,
              steps: [
                ...section.steps,
                { 
                  field1: "", 
                  field2: "", 
                  field3: "", 
                  field4: "", 
                  field5: "", 
                  field6: "", 
                  requiresPhoto: false, 
                  photoInstructions: "" 
                },
              ],
            }
          : section
      )
    );
  };

  // Usuwanie sekcji
  const removeSection = (sectionIndex) => {
    setSections((prevSections) =>
      prevSections.filter((_, index) => index !== sectionIndex)
    );
  };

  // Usuwanie kroku w sekcji
  const removeStepFromSection = (sectionIndex, stepIndex) => {
    setSections((prevSections) =>
      prevSections.map((section, index) =>
        index === sectionIndex
          ? {
              ...section,
              steps: section.steps.filter((_, idx) => idx !== stepIndex),
            }
          : section
      )
    );
  };

  // Obsługa zmian w polach kroków
  const handleStepChange = (sectionIndex, stepIndex, field, value) => {
    setSections((prevSections) =>
      prevSections.map((section, index) =>
        index === sectionIndex
          ? {
              ...section,
              steps: section.steps.map((step, idx) =>
                idx === stepIndex ? { ...step, [field]: value } : step
              ),
            }
          : section
      )
    );
  };

  // Obsługa zmian tytułu lub oznaczenia sekcji
  const handleSectionChange = (sectionIndex, field, value) => {
    setSections((prevSections) =>
      prevSections.map((section, index) =>
        index === sectionIndex ? { ...section, [field]: value } : section
      )
    );
  };

  // Zapis instrukcji do backendu z walidacją
  const saveInstruction = async (proper) => {
    if (!selectedWorkScope) {
      alert("Please select a scope of work before saving.");
      return;
    }
  
    if (selectedTurbines.length === 0) {
      alert("Please select at least one turbine before saving.");
      return;
    }

    // Walidacja sekcji i kroków
    for (const [sectionIndex, section] of sections.entries()) {
      if (!section.title || section.title.trim() === "") {
        alert(`Error: Section title is required for section ${sectionIndex + 1}.`);
        return;
      }
      if (!section.mark || section.mark.trim() === "") {
        alert(`Error: Mark is required for section ${sectionIndex + 1}.`);
        return;
      }
      for (const [stepIndex, step] of section.steps.entries()) {
        if (step.requiresPhoto && (!step.photoInstructions || step.photoInstructions.trim() === "")) {
          alert(`Error: Photo instructions are required for step ${stepIndex + 1} in section ${sectionIndex + 1} when "Requires Photo" is checked.`);
          return;
        }
      }
    }

    const payload = {
      scope_of_work: selectedWorkScope?.id,
      turbine: selectedTurbines,
      proper,
      data: {
        sections: sections.map((section) => ({
          title: section.title,
          mark: section.mark,
          steps: section.steps,
        })),
      },
    };
    console.log("payload payload:", payload);
    try {
      const response = await fetch(`${API_BASE_URL}/instructions/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Błąd: ${response.status}`);
      }

      const data = await response.json();
      console.log("Odpowiedź backendu:", data);
      alert("Instrukcja została zapisana.");
      setSections([
        {
          title: "",
          mark: "",
          steps: [
            { 
              field1: "", 
              field2: "", 
              field3: "", 
              field4: "", 
              field5: "", 
              field6: "", 
              requiresPhoto: false, 
              photoInstructions: "" 
            },
          ],
        },
      ]);
      setInstToView(null);
      setSelectedTurbines([]);
      fetchInstructions();
    } catch (error) {
      console.error("Błąd podczas zapisu instrukcji:", error);
      alert("Nie udało się zapisać instrukcji.");
    }
  };
  
  const handleEdit = () => {
    if (instToView) {
      setSections(instToView.data.sections);
      setInstToView(null);
    }
  };

  const autoResize = (textarea) => {
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useEffect(() => {
    const textareas = document.querySelectorAll("textarea.auto-resize");
    textareas.forEach((textarea) => autoResize(textarea));
    console.log("Aktualne sekcje:", sections);
    console.log("Aktualna instrukcja:", instToView);
  }, [sections, instToView]);

  return (
    <div
      className="bg-white rounded-xl border border-steel-200 flex flex-col h-full overflow-hidden shadow-sm"
    >
      <div className="flex-1 overflow-auto">
        <div className="flex flex-col h-full overflow-hidden relative">
          {/* Scope of Work Selection */}
          <div className="bg-gradient-to-r from-blue-50 to-white px-4 py-2.5 border-b border-blue-100">
            <div className="flex flex-col gap-2">
              {/* Top Section: Title and Select in one line */}
              <div className="flex flex-row items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                      <path d="M9 11l3 3L22 4"/>
                      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                    </svg>
                  </div>
                  <h2 className="text-sm font-semibold text-graphite-900">Choose Scope of Work:</h2>
                </div>
                <div className="relative">
                  <select
                    value={selectedWorkScope?.id || ""}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedScope = workScopes.find((scope) => scope.id === parseInt(selectedId, 10));
                      setSelectedWorkScope(selectedScope);
                      setIsDescriptionExpanded(false); // Reset expanded state on scope change
                    }}
                    className="pl-3 pr-8 py-1.5 text-sm border border-steel-300 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 bg-white"
                  >
                    <option value="">-- Choose Scope --</option>
                    {workScopes.length > 0 ? (
                      workScopes.map((scope) => (
                        <option key={scope.id} value={scope.id}>
                          {scope.name}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>
                        No scopes available
                      </option>
                    )}
                  </select>
                </div>
              </div>
              {/* Bottom Section: Description if available */}
              {selectedWorkScope && selectedWorkScope.description && (
                <div className="ml-9">
                  <p
                    className={`text-graphite-600 text-xs ${isDescriptionExpanded ? '' : 'line-clamp-2'}`}
                  >
                    {selectedWorkScope.description}
                  </p>
                  {!isDescriptionExpanded && selectedWorkScope.description.split(' ').length > 20 && (
                    <button
                      className="text-brand-500 hover:text-brand-600 text-xs font-medium mt-0.5"
                      onClick={() => setIsDescriptionExpanded(true)}
                    >
                      Show more
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          
          <div className="px-4 py-2 border-b border-steel-100 bg-steel-50">
            <div className="flex justify-between items-center">
              {instToView ? (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-graphite-700 font-medium text-sm">
                      {instToView.proper ? "Your Instruction" : "Your Instruction (Default)"}
                    </span>
                    <span className="text-graphite-400 text-xs">
                      Created {new Date(instToView.created_time).toLocaleString()} by {instToView.created_by_data}
                    </span>
                  </div>
                  <button
                    onClick={handleEdit}
                    className="px-3 py-1.5 border border-steel-300 text-graphite-700 rounded-full hover:bg-steel-100 flex items-center text-xs transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-3 w-3 mr-1"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Edit
                  </button>
                </>
              ) : (
                <h2 className="text-sm font-semibold text-graphite-900">Write Instruction</h2>
              )}
            </div>
          </div>

          {/* Sections */}
          <div className="flex-1 overflow-auto px-3 py-3 bg-white">
            <div className="space-y-3">
              {(instToView ? instToView.data.sections : sections).map((section, sectionIndex) => (
                <div key={sectionIndex} className="border border-steel-200 rounded-lg p-3 relative bg-steel-50 shadow-sm">
                  {!instToView && (
                    <button
                      onClick={() => removeSection(sectionIndex)}
                      className="absolute top-2 right-2 w-5 h-5 flex items-center justify-center rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition z-10"
                    >
                      <X size={12} />
                    </button>
                  )}

                  {/* Section Title & Mark */}
                  <div className="flex justify-between items-center mb-3">
                    <input
                      type="text"
                      value={section.title || ""}
                      onFocus={() => setIsExpanded(true)}
                      onChange={(e) => !instToView && handleSectionChange(sectionIndex, "title", e.target.value)}
                      placeholder="Section Title"
                      className={`border rounded-lg px-2.5 py-1.5 text-sm flex-grow focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 ${
                        !instToView && (!section.title || section.title.trim() === "")
                          ? "border-red-400 bg-red-50"
                          : "border-steel-300 bg-white"
                      } ${instToView ? "bg-steel-100 cursor-not-allowed text-graphite-600" : ""}`}
                      readOnly={!!instToView}
                    />
                    <input
                      type="text"
                      value={section.mark || ""}
                      onFocus={() => setIsExpanded(true)}
                      onChange={(e) => !instToView && handleSectionChange(sectionIndex, "mark", e.target.value)}
                      placeholder="Mark"
                      className={`border rounded-lg px-2.5 py-1.5 text-sm w-1/6 min-w-[20px] text-center ml-3 mr-6 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 ${
                        !instToView && (!section.mark || section.mark.trim() === "")
                          ? "border-red-400 bg-red-50"
                          : "border-steel-300 bg-white"
                      } ${instToView ? "bg-steel-100 cursor-not-allowed text-graphite-600" : ""}`}
                      readOnly={!!instToView}
                    />
                  </div>

                  {/* Steps */}
                  {section.steps.map((step, stepIndex) => (
                    <div key={stepIndex}>
                      <div className="mb-4 flex items-start space-x-3">
                        <div className="flex-grow">
                          {/* First row of inputs */}
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            {["Isolation ID and description", "Type and magnitude of energy", "Isolation position"].map(
                              (placeholderText, fieldIndex) => (
                                <input
                                  key={`${sectionIndex}-${stepIndex}-${fieldIndex}-${instToView?.id || "default"}`}
                                  type="text"
                                  value={step[`field${fieldIndex + 1}`] || ""}
                                  onFocus={() => setIsExpanded(true)}
                                  onChange={(e) => {
                                    if (!instToView) {
                                      handleStepChange(sectionIndex, stepIndex, `field${fieldIndex + 1}`, e.target.value);
                                    }
                                  }}
                                  placeholder={placeholderText}
                                  className={`w-full border border-steel-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 placeholder-graphite-400 ${
                                    instToView ? "bg-steel-100 cursor-not-allowed text-graphite-600" : "bg-white"
                                  }`}
                                  readOnly={!!instToView}
                                />
                              )
                            )}
                          </div>

                          {/* Second row of inputs */}
                          <div className="grid grid-cols-3 gap-2 mb-2">
                            {["Method to verify de-energisation", "Location to verify de-energisation", "Method to dissipate stored energy"].map(
                              (placeholderText, fieldIndex) => (
                                <input
                                  key={`${sectionIndex}-${stepIndex}-field${fieldIndex + 4}`}
                                  type="text"
                                  value={step[`field${fieldIndex + 4}`] || ""}
                                  onFocus={() => setIsExpanded(true)}
                                  onChange={(e) => {
                                    if (!instToView) {
                                      handleStepChange(sectionIndex, stepIndex, `field${fieldIndex + 4}`, e.target.value);
                                    }
                                  }}
                                  placeholder={placeholderText}
                                  className={`w-full border border-steel-300 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 placeholder-graphite-400 ${
                                    instToView ? "bg-steel-100 cursor-not-allowed text-graphite-600" : "bg-white"
                                  }`}
                                  readOnly={!!instToView}
                                />
                              )
                            )}
                          </div>

                          {/* Checkbox for photo */}
                          <div className="mt-2">
                            {!instToView ? (
                              <div className="flex items-center space-x-3">
                                <label className={`flex items-center gap-1.5 px-2 py-1 rounded-md border cursor-pointer transition-all text-xs ${
                                  step.requiresPhoto ? 'border-green-500 bg-green-50' : 'border-steel-200 hover:border-steel-300 bg-white'
                                }`}>
                                  <input
                                    type="checkbox"
                                    checked={step.requiresPhoto || false}
                                    onChange={(e) => handleStepChange(sectionIndex, stepIndex, "requiresPhoto", e.target.checked)}
                                    className="sr-only"
                                  />
                                  <div className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 ${step.requiresPhoto ? 'bg-green-500' : 'bg-steel-200'}`}>
                                    {step.requiresPhoto && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                  </div>
                                  <span className="text-graphite-700 whitespace-nowrap">Requires Photo</span>
                                </label>
                                {step.requiresPhoto && (
                                  <input
                                    type="text"
                                    value={step.photoInstructions || ""}
                                    onFocus={() => setIsExpanded(true)}
                                    onChange={(e) => {
                                      handleStepChange(sectionIndex, stepIndex, "photoInstructions", e.target.value);
                                    }}
                                    placeholder="Instructions for taking the photo"
                                    className={`border rounded-lg px-2.5 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-graphite-900 placeholder-graphite-400 ${
                                      !step.photoInstructions || step.photoInstructions.trim() === ""
                                        ? "border-red-400 bg-red-50"
                                        : "border-steel-300 bg-white"
                                    }`}
                                  />
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span
                                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${
                                    step.requiresPhoto ? "bg-green-100 text-green-700" : "bg-steel-100 text-graphite-500"
                                  }`}
                                >
                                  {step.requiresPhoto ? (
                                    <>
                                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                                      Photo Required
                                    </>
                                  ) : "No Photo Required"}
                                </span>
                                {step.requiresPhoto && step.photoInstructions && (
                                  <span className="text-xs text-graphite-600">
                                    {step.photoInstructions}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Keep the original arrows and trash button */}
                        {!instToView && (
                          <div className="flex flex-col items-center space-y-1">
                            <button
                              onClick={() => {
                                if (stepIndex > 0) {
                                  setSections((prevSections) =>
                                    prevSections.map((section, index) =>
                                      index === sectionIndex
                                        ? {
                                            ...section,
                                            steps: [
                                              ...section.steps.slice(0, stepIndex - 1),
                                              section.steps[stepIndex],
                                              section.steps[stepIndex - 1],
                                              ...section.steps.slice(stepIndex + 1),
                                            ],
                                          }
                                        : section
                                    )
                                  );
                                }
                              }}
                              className="text-blue-500 hover:text-blue-600 transition text-xs"
                            >
                              ▲
                            </button>
                            <button
                              onClick={() => removeStepFromSection(sectionIndex, stepIndex)}
                              className="text-red-600 hover:text-red-800 transition"
                            >
                              <Trash2 size={12} />
                            </button>
                            <button
                              onClick={() => {
                                if (stepIndex < section.steps.length - 1) {
                                  setSections((prevSections) =>
                                    prevSections.map((section, index) =>
                                      index === sectionIndex
                                        ? {
                                            ...section,
                                            steps: [
                                              ...section.steps.slice(0, stepIndex),
                                              section.steps[stepIndex + 1],
                                              section.steps[stepIndex],
                                              ...section.steps.slice(stepIndex + 2),
                                            ],
                                          }
                                        : section
                                    )
                                  );
                                }
                              }}
                              className="text-blue-500 hover:text-blue-600 transition text-xs"
                            >
                              ▼
                            </button>
                          </div>
                        )}
                      </div>

                      {stepIndex < section.steps.length - 1 && <hr className="border-t border-steel-200 my-3" />}
                    </div>
                  ))}

                  {/* Add Step Button */}
                  {!instToView && (
                    <button
                      onClick={() => addStepToSection(sectionIndex)}
                      className="mt-2 px-3 py-1.5 bg-graphite-700 text-white rounded-full hover:bg-graphite-800 transition text-xs flex items-center gap-1.5"
                    >
                      <Plus size={12} />
                      Add Step
                    </button>
                  )}
                </div>
              ))}

              {/* Add Section Button */}
              {!instToView && (
                <div className="flex space-x-3 mb-3">
                  <button
                    onClick={addSection}
                    className="px-4 py-2 bg-brand-500 text-white rounded-full hover:bg-brand-600 transition flex items-center gap-1.5 font-medium text-sm"
                  >
                    <Plus size={14} />
                    Add New Section
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer with Fixed Save Buttons */}
          {!instToView && (
            <div className="sticky bottom-0 left-0 right-0 px-4 py-2.5 border-t border-steel-200 bg-steel-50 flex justify-end space-x-2 z-10">
              <button
                onClick={() => saveInstruction(false)}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition font-medium text-sm"
              >
                Save as default
              </button>
              <button
                onClick={() => saveInstruction(true)}
                className="px-4 py-1.5 bg-green-600 text-white rounded-full hover:bg-green-700 transition font-medium text-sm"
              >
                Save as unique
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScopeOfWork;